/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Platform,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import globals from '@utils/globals';
import {
  useMemo,
} from 'react';
import {
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import useTheme from './useTheme';
import useTypedSelector from './useTypedSelector';

export default function useStyles(getStyles, extraVars={}) {
  const theme = useTheme();
  const isDarkMode = useTypedSelector((state) => state.settingsLightDarkMode === 'dark');
  const scale = useTypedSelector((state) => state.settingsPreferredScale);
  const globalScale = globals.fontScales[scale] || 1.0; // "medium" was originally "normal", so i think there's an issue getting the global scale above; (this requires a redux migration to fix, but i'm worried about other non-allowed values...would be better to have a utility function, or to save the _actual_ scale decimal in redux instead of a string that maps to the scale value)

  const safeAreaInsets = useSafeAreaInsets();
  const {width, height}  = useWindowDimensions(); // @TODO: seems like a bug in react native where this is returning the screen width (or maybe _initial_ window width) instead of window width (e.g. always 2560 on my 32" screen instead of the resized widnow width)...macos only? not sure about chromebook

  const styles = useMemo(() => {
    const systemVars = {
      globalScale,
      colors: theme.colors,
      fonts: theme.fonts,
      themeBackgroundImage: theme.backgroundImage,
      isDarkMode,
      safeAreaInsets,
      windowWidth: Math.min(width, globals.maxInnerWidth), // @TODO: don't like this name
      windowWidthRaw: width, // @TODO: don't like this name
      windowHeight: height,
    };

    const stylesObject = getStyles({
      ...systemVars,
      ...extraVars,
    });

    return {
      ...systemVars,
      ...extraVars,
      ...(Platform.OS === 'web' ? stylesObject : StyleSheet.create(stylesObject)),
    } as const;
  }, [getStyles, globalScale, theme, isDarkMode, extraVars, safeAreaInsets, width, height]); // @TODO: is extraVars always a new ref?
  /* @TODO: why isn't this working? Home/index.native.tsx has a style styles.asdfqwer that defintiely doesn't exist

  type TypedStyles = keyof typeof styles;
  const typedStyles: TypedStyles = styles;
  return typedStyles;
  */

  return styles as typeof styles;
}
